import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

// 🪵 DEBUG: Confirm Supabase env variables
console.log("DEBUG → Supabase env loaded:", {
  supabaseUrl,
  hasAnonKey: !!supabaseAnonKey,
});

// 🔧 Global connection tracker
let connectionStatus = {
  isOnline: true,
  lastCheck: Date.now(),
  failureCount: 0,
  isRecovering: false
};

// ✅ NETWORK CHECK FIX — use `/auth/v1/health` (always available)
export const networkStatus = {
  checkConnection: async () => {
    try {
      const response = await fetch(`${supabaseUrl}/auth/v1/health`, { method: 'GET' });
      return response?.ok;
    } catch (error) {
      console.warn('Connection check failed:', error?.message);
      return false;
    }
  },

  onConnectionChange: (callback) => {
    const handleOnline = () => {
      connectionStatus.isOnline = true;
      callback?.(true);
    };

    const handleOffline = () => {
      connectionStatus.isOnline = false;
      callback?.(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }
};

// ✅ FIXED: Smart retry fetch now bypasses Supabase auth endpoints
const createSmartRetryFetch = (originalFetch) => {
  return async (url, options = {}) => {
    console.log("DEBUG → Smart fetch START:", url, options?.method || 'GET');

    // ⚠️ SKIP retry logic for Supabase auth endpoints (important fix)
    if (url?.includes('/auth/v1/')) {
      return originalFetch(url, options);
    }

    const controller = new AbortController();
    let timeoutId;

    // Circuit breaker
    if (connectionStatus?.failureCount >= 5 &&
        Date.now() - connectionStatus?.lastCheck < 30000) {
      console.warn('Circuit breaker active - too many recent failures');
      throw new Error('Service temporarily unavailable - please try again in a moment');
    }

    try {
      // Slightly increased timeout for reliability
      const timeout = Math.min(15000 + (connectionStatus?.failureCount * 3000), 30000);
      timeoutId = setTimeout(() => controller?.abort(), timeout);

      const fetchOptions = { ...options, signal: controller?.signal };
      const response = await originalFetch(url, fetchOptions);
      clearTimeout(timeoutId);

      console.log("DEBUG → Smart fetch RESPONSE:", url, response?.status);

      if (response?.ok) {
        connectionStatus.failureCount = 0;
        connectionStatus.isOnline = true;
        connectionStatus.lastCheck = Date.now();
        return response;
      }

      if (response?.status >= 400) {
        connectionStatus.failureCount++;
        connectionStatus.lastCheck = Date.now();
        console.warn("DEBUG → Smart fetch HTTP error:", response?.status, "for", url);
        return response;
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      connectionStatus.failureCount++;
      connectionStatus.lastCheck = Date.now();
      connectionStatus.isOnline = false;

      console.error("DEBUG → Smart fetch ERROR:", error?.message, "URL:", url);

      if (error?.name === 'AbortError') {
        throw new Error('Request timed out - please check your connection and try again');
      }

      if (error?.message?.includes('Failed to fetch') || 
          error?.name === 'TypeError' || error?.message?.includes('NetworkError')) {
        const hasConnection = await networkStatus?.checkConnection();
        if (!hasConnection) {
          throw new Error('No internet connection detected - please check your network settings');
        } else {
          throw new Error('Service temporarily unavailable - please try again');
        }
      }

      throw error;
    }
  };
};

// 🪵 DEBUG: Confirm we are creating Supabase client
console.log("DEBUG → Creating Supabase client with enhanced fetch...");

// ✅ FIXED: Client creation (keeps your configs but safe for auth)
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
  global: {
    headers: {
      'X-Client-Info': 'smart-cities-hub@1.0.0'
    },
    fetch: createSmartRetryFetch(fetch)
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 2
    }
  }
});

console.log("DEBUG → Supabase client initialized successfully");

// ✅ STATUS HELPERS (unchanged)
export const getConnectionStatus = () => ({
  isOnline: connectionStatus?.isOnline,
  isHealthy: connectionStatus?.failureCount < 3,
  failureCount: connectionStatus?.failureCount,
  lastCheck: connectionStatus?.lastCheck,
  shouldRetry: connectionStatus?.failureCount < 10,
  isRecovering: connectionStatus?.isRecovering
});

export const attemptConnectionRecovery = async () => {
  connectionStatus.isRecovering = true;
  
  try {
    const hasConnection = await networkStatus?.checkConnection();
    
    if (hasConnection) {
      const { data, error } = await supabase?.from('user_profiles')?.select('count(*)')?.limit(1);
      if (!error) {
        connectionStatus.isOnline = true;
        connectionStatus.failureCount = 0;
        connectionStatus.lastCheck = Date.now();
        console.log('Connection recovery successful');
        return true;
      }
    }
    
    console.warn('Connection recovery failed - service still unavailable');
    return false;
  } catch (error) {
    console.error('Connection recovery error:', error?.message);
    return false;
  } finally {
    connectionStatus.isRecovering = false;
  }
};

// ✅ AUTH STATE LOGGING (kept as-is)
supabase?.auth?.onAuthStateChange((event, session) => {
  console.log('Auth state changed:', event);
  
  if (event === 'SIGNED_OUT') {
    localStorage.removeItem('supabase.auth.token');
    console.log('User signed out successfully');
  }
  
  if (event === 'TOKEN_REFRESHED') {
    console.log('Auth token refreshed successfully');
    connectionStatus.failureCount = 0;
  }
  
  if (event === 'SIGNED_IN') {
    console.log('User signed in:', session?.user?.email);
    connectionStatus.failureCount = 0;
  }
  
  if (event === 'SIGNED_UP') {
    console.log('User signed up:', session?.user?.email);
  }
});

// Placeholder for future monitoring setup
function initializeConnectionMonitoring(...args) {
  console.warn('Placeholder: initializeConnectionMonitoring is not implemented yet.', args);
  return null;
}

export { initializeConnectionMonitoring };